import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getProviders, updateProvider, connectProvider, disconnectProvider, getSyncHistory } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useSync } from '../contexts/SyncContext';
import { toast } from 'sonner';
import { Cloud, Settings, Check, X, Loader2, RefreshCw, Clock, AlertTriangle, SkipForward, History, ChevronDown } from 'lucide-react';

const providerNames = {
  aws: 'Amazon Web Services',
  azure: 'Microsoft Azure',
  gcp: 'Google Cloud Platform',
  ovh: 'OVH Cloud'
};

const providerColors = {
  aws: '#FF9900',
  azure: '#007FFF',
  gcp: '#4285F4',
  ovh: '#123F6D'
};

const providerDescriptions = {
  aws: 'Connectez votre compte AWS pour importer les données de facturation.',
  azure: 'Connectez votre abonnement Azure via l\'API Cost Management.',
  gcp: 'Connectez votre projet GCP pour accéder aux données de facturation.',
  ovh: 'Connectez votre compte OVH via les clés API.'
};

const providerFieldsConfig = {
  aws: {
    accessKey: { label: 'Access Key ID', placeholder: 'AKIAIOSFODNN7EXAMPLE', help: 'Clé d\'accès IAM. Créez-la dans AWS Console → IAM → Users → Security credentials.' },
    secretKey: { label: 'Secret Access Key', placeholder: 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY', help: 'Clé secrète associée. Visible uniquement à la création de la clé IAM.' },
    region: { label: 'Région', placeholder: 'eu-west-1', help: 'Région AWS principale (ex: eu-west-1, us-east-1). Utilisée pour l\'API Cost Explorer.' },
  },
  azure: {
    accessKey: { label: 'Client ID (App ID)', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', help: 'ID de l\'application enregistrée dans Azure AD → App registrations.' },
    secretKey: { label: 'Client Secret', placeholder: 'Votre client secret', help: 'Secret généré dans Azure AD → App registrations → Certificates & secrets.' },
    region: { label: 'Scope (Subscription ID)', placeholder: '/subscriptions/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', help: 'Chemin complet de l\'abonnement. Format : /subscriptions/{subscription-id}. L\'app doit avoir le rôle "Cost Management Reader" sur cet abonnement.' },
  },
  gcp: {
    accessKey: { label: 'Client Email (Service Account)', placeholder: 'mon-sa@projet.iam.gserviceaccount.com', help: 'Email du compte de service GCP. Créez-le dans GCP Console → IAM → Service accounts.' },
    secretKey: { label: 'Private Key (JSON)', placeholder: '{"type":"service_account",...}', help: 'Contenu JSON de la clé du compte de service. Nécessite l\'export BigQuery billing activé.' },
    region: { label: 'Project ID', placeholder: 'mon-projet-gcp', help: 'ID du projet GCP contenant l\'export de facturation BigQuery.' },
  },
  ovh: {
    accessKey: { label: 'Application Key', placeholder: 'votre_application_key', help: 'Clé d\'application OVH. Créez-la sur https://api.ovh.com/createApp/' },
    secretKey: { label: 'Application Secret', placeholder: 'votre_application_secret', help: 'Secret d\'application OVH associé à la clé ci-dessus.' },
    region: { label: 'Endpoint', placeholder: 'ovh-eu', help: 'Point d\'accès API : ovh-eu (Europe), ovh-ca (Canada), ovh-us (USA).' },
    consumerKey: { label: 'Consumer Key', placeholder: 'votre_consumer_key', help: 'Jeton d\'accès validé. Générez-le via https://api.ovh.com/createToken/ avec les droits GET /me/bill/*.' },
  },
};

const ProviderCard = ({ provider, onUpdate, onSyncDone }) => {
  const { isAdmin } = useAuth();
  const { syncOne, isSyncingProvider } = useSync();
  const [configOpen, setConfigOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [region, setRegion] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const syncing = isSyncingProvider(provider.id);
  
  const name = providerNames[provider.provider] || provider.provider;
  const color = providerColors[provider.provider] || '#666666';
  const description = providerDescriptions[provider.provider] || '';

  const handleConnect = async () => {
    setLoading(true);
    try {
      const credentials = { access_key: accessKey, secret_key: secretKey, region: region };
      if (consumerKey) credentials.consumer_key = consumerKey;
      await updateProvider(provider.id, { credentials: credentials });
      await connectProvider(provider.id);
      toast.success(name + ' connecté avec succès');
      onUpdate();
      setConfigOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      await disconnectProvider(provider.id);
      toast.success(name + ' déconnecté');
      onUpdate();
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    const result = await syncOne(provider);
    if (result.ok) {
      toast.success(`${name} : ${result.records} enregistrements importés`);
    } else {
      toast.error(result.error);
    }
    if (onSyncDone) onSyncDone();
  };

  return (
    <Card className="relative overflow-hidden" data-testid={'provider-card-' + provider.provider}>
      <div 
        className="absolute top-0 left-0 w-1 h-full" 
        style={{ backgroundColor: color }}
      />
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: color + '20' }}
            >
              <Cloud className="w-6 h-6" style={{ color: color }} />
            </div>
            <div>
              <CardTitle className="text-lg font-[Outfit]">{name}</CardTitle>
              <CardDescription className="mt-1">
                {provider.is_connected ? 'Connecté' : 'Non connecté'}
              </CardDescription>
            </div>
          </div>
          <Badge variant={provider.is_connected ? 'default' : 'secondary'}>
            {provider.is_connected ? (
              <span className="flex items-center"><Check className="w-3 h-3 mr-1" /> Actif</span>
            ) : (
              <span className="flex items-center"><X className="w-3 h-3 mr-1" /> Inactif</span>
            )}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">{description}</p>
        
        {isAdmin ? (
          <div className="flex gap-2 flex-wrap">
            <Dialog open={configOpen} onOpenChange={setConfigOpen}>
              <DialogTrigger asChild>
                <Button variant={provider.is_connected ? 'outline' : 'default'} data-testid={'configure-' + provider.provider}>
                  <Settings className="w-4 h-4 mr-2" />
                  Configurer
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Configurer {name}</DialogTitle>
                  <DialogDescription>
                    {provider.is_connected
                      ? 'Modifiez vos identifiants API. Les champs vides conservent la valeur actuelle.'
                      : 'Entrez vos identifiants API pour connecter ce provider.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {(() => {
                    const fields = providerFieldsConfig[provider.provider] || providerFieldsConfig.aws;
                    const stateMap = {
                      accessKey: [accessKey, setAccessKey],
                      secretKey: [secretKey, setSecretKey],
                      region: [region, setRegion],
                      consumerKey: [consumerKey, setConsumerKey],
                    };
                    return Object.entries(fields).map(([key, cfg]) => {
                      const [value, setter] = stateMap[key] || ['', () => {}];
                      return (
                        <div key={key} className="space-y-2">
                          <Label htmlFor={key}>{cfg.label}</Label>
                          <Input
                            id={key}
                            type={key === 'secretKey' || key === 'consumerKey' ? 'password' : 'text'}
                            value={value}
                            onChange={(e) => setter(e.target.value)}
                            placeholder={provider.is_connected ? '••••••••' : cfg.placeholder}
                          />
                          <p className="text-xs text-muted-foreground">{cfg.help}</p>
                        </div>
                      );
                    });
                  })()}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setConfigOpen(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleConnect} disabled={loading}>
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {provider.is_connected ? 'Mettre à jour' : 'Connecter'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            {provider.is_connected && (
              <>
                <Button
                  variant="outline"
                  onClick={handleSync}
                  disabled={syncing}
                  data-testid={'sync-' + provider.provider}
                >
                  {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Synchroniser
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDisconnect}
                  disabled={loading}
                  data-testid={'disconnect-' + provider.provider}
                >
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Déconnecter
                </Button>
              </>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            Seuls les administrateurs peuvent modifier la configuration.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const ErrorCell = ({ message }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        className="flex items-start gap-1 text-destructive text-left hover:underline cursor-pointer"
        onClick={() => setOpen(true)}
      >
        <span className="truncate max-w-[200px] block">{message}</span>
        <ChevronDown className="w-3 h-3 mt-0.5 shrink-0" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Détail de l'erreur
            </DialogTitle>
          </DialogHeader>
          <pre className="text-xs bg-muted rounded-md p-4 whitespace-pre-wrap break-words overflow-auto max-h-64">
            {message}
          </pre>
        </DialogContent>
      </Dialog>
    </>
  );
};

const triggerLabels = {
  manual: 'Manuel',
  'sync-all': 'Sync All',
  auto: 'Auto',
};

const statusConfig = {
  ok: { label: 'Succès', variant: 'default', icon: Check },
  error: { label: 'Erreur', variant: 'destructive', icon: AlertTriangle },
  skipped: { label: 'Ignoré', variant: 'secondary', icon: SkipForward },
};

function formatDuration(startedAt, finishedAt) {
  const start = new Date(startedAt);
  const end = new Date(finishedAt);
  const diffMs = end - start;
  if (diffMs < 1000) return `${diffMs}ms`;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatDate(isoString) {
  const d = new Date(isoString);
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const ProvidersPage = () => {
  const { isAdmin } = useAuth();
  const { syncAll, isSyncing } = useSync();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncHistory, setSyncHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const fetchProviders = async () => {
    try {
      const response = await getProviders();
      setProviders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Erreur lors du chargement des providers');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await getSyncHistory(50);
      setSyncHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Silently fail for history — not critical
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
    fetchHistory();
  }, []);

  const hasConnectedProviders = providers.some(p => p.is_connected);

  const handleSyncAll = async () => {
    const result = await syncAll();
    if (result.ok) {
      const ok = result.results.filter(r => r.status === 'ok');
      const errors = result.results.filter(r => r.status === 'error');
      const totalRecords = ok.reduce((sum, r) => sum + (r.records_imported || 0), 0);
      if (errors.length > 0) {
        toast.warning(`${ok.length} providers synchronisés (${totalRecords} enregistrements), ${errors.length} en erreur`);
      } else {
        toast.success(`${ok.length} providers synchronisés : ${totalRecords} enregistrements importés`);
      }
    } else {
      toast.error(result.error);
    }
    fetchHistory();
  };

  const renderSkeletons = () => {
    const items = [];
    for (let i = 0; i < 4; i++) {
      items.push(
        <Card key={i} className="animate-pulse">
          <CardHeader>
            <div className="h-12 w-12 bg-muted rounded-lg" />
            <div className="h-4 w-32 bg-muted rounded mt-2" />
          </CardHeader>
          <CardContent>
            <div className="h-16 bg-muted rounded" />
          </CardContent>
        </Card>
      );
    }
    return items;
  };

  return (
    <Layout>
      <Header
        title="Providers Cloud"
        subtitle="Configurez vos connexions aux différents providers cloud"
      />

      {isAdmin && hasConnectedProviders && (
        <div className="flex justify-end mb-4">
          <Button
            onClick={handleSyncAll}
            disabled={isSyncing}
            data-testid="sync-all"
          >
            {isSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Synchroniser tout
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="providers-grid">
        {loading ? renderSkeletons() : providers.map(provider => (
          <ProviderCard
            key={provider.id}
            provider={provider}
            onUpdate={fetchProviders}
            onSyncDone={fetchHistory}
          />
        ))}
      </div>

      {/* Sync History */}
      <Card className="mt-8">
        <CardHeader>
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-muted-foreground" />
            <CardTitle className="text-lg font-[Outfit]">Historique de synchronisation</CardTitle>
          </div>
          <CardDescription>Dernières synchronisations des providers cloud</CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : syncHistory.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Aucune synchronisation enregistrée pour le moment.
            </p>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Records</TableHead>
                      <TableHead>Déclencheur</TableHead>
                      <TableHead>Durée</TableHead>
                      <TableHead>Erreur</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {syncHistory.map((entry) => {
                      const cfg = statusConfig[entry.status] || statusConfig.skipped;
                      const StatusIcon = cfg.icon;
                      return (
                        <TableRow key={entry.id}>
                          <TableCell className="whitespace-nowrap text-xs">
                            {formatDate(entry.started_at)}
                          </TableCell>
                          <TableCell className="font-medium uppercase text-xs">
                            {entry.provider_name}
                          </TableCell>
                          <TableCell>
                            <Badge variant={cfg.variant} className="gap-1">
                              <StatusIcon className="w-3 h-3" />
                              {cfg.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-xs">
                            {entry.records_imported}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="gap-1 text-xs">
                              {entry.trigger === 'auto' && <Clock className="w-3 h-3" />}
                              {entry.trigger === 'manual' && <RefreshCw className="w-3 h-3" />}
                              {entry.trigger === 'sync-all' && <RefreshCw className="w-3 h-3" />}
                              {triggerLabels[entry.trigger] || entry.trigger}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            {formatDuration(entry.started_at, entry.finished_at)}
                          </TableCell>
                          <TableCell className="text-xs max-w-[240px]">
                            {entry.error_message ? (
                              <ErrorCell message={entry.error_message} />
                            ) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {syncHistory.map((entry) => {
                  const cfg = statusConfig[entry.status] || statusConfig.skipped;
                  const StatusIcon = cfg.icon;
                  return (
                    <div key={entry.id} className="rounded-lg border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium uppercase text-sm">{entry.provider_name}</span>
                        <Badge variant={cfg.variant} className="gap-1">
                          <StatusIcon className="w-3 h-3" />
                          {cfg.label}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span>{formatDate(entry.started_at)}</span>
                        <span>·</span>
                        <span>{formatDuration(entry.started_at, entry.finished_at)}</span>
                        <span>·</span>
                        <Badge variant="outline" className="gap-1 text-xs">
                          {entry.trigger === 'auto' && <Clock className="w-3 h-3" />}
                          {(entry.trigger === 'manual' || entry.trigger === 'sync-all') && <RefreshCw className="w-3 h-3" />}
                          {triggerLabels[entry.trigger] || entry.trigger}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{entry.records_imported} records importés</span>
                      </div>
                      {entry.error_message && (
                        <p className="text-xs text-destructive break-words">{entry.error_message}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
};
